import { forwardRef, useImperativeHandle, useRef } from 'react'
import { styled } from 'baseui'
import Icons from '@/scenes/Editor/components/Icons'
import WithPopover, { WithPopoverHandle } from '@/scenes/Editor/components/FloatingInspectorButton/WithPopover'
import WrapPopover from '@/scenes/Editor/components/FloatingInspectorButton/SharedComponents/Popover/WrapPopover'

const Anchor = styled('div', {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 11,
})

const IconButton = styled('button', {
  width: '24px',
  height: '24px',
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  background: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
})

const POPOVER_WIDTH = 276

export interface TileAdjustButtonProps {
  /** Rendered inside the popover once it opens. */
  popoverContent?: React.ReactNode
  /** Fired before the popover toggles, for tracking. */
  onClick?: () => void
}

/**
 * Centered adjust affordance for the active thumbnail tile, plus the popover it
 * opens. The tile body usually toggles the same popover, so the handle is
 * forwarded: keep a ref on this component and call `togglePopover()` from the
 * tile's own click handler.
 */
const TileAdjustButton = forwardRef<WithPopoverHandle, TileAdjustButtonProps>(
  function TileAdjustButton({ popoverContent, onClick }, ref) {
    const popoverRef = useRef<WithPopoverHandle>(null)
    useImperativeHandle(ref, () => ({
      closePopover: () => popoverRef.current?.closePopover(),
      togglePopover: () => popoverRef.current?.togglePopover(),
    }), [])

    return (
      <Anchor>
        <WithPopover
          ref={popoverRef}
          popoverContent={(buttonRef) => (
            <WrapPopover buttonRef={buttonRef} placement="bottom" width={POPOVER_WIDTH} padding="20,16">
              {popoverContent}
            </WrapPopover>
          )}
        >
          <IconButton
            type="button"
            onClick={(e: React.MouseEvent) => {
              // The tile body has its own click handler; this must not run it too.
              e.stopPropagation()
              onClick?.()
              popoverRef.current?.togglePopover()
            }}
          >
            <Icons.AdjustBar />
          </IconButton>
        </WithPopover>
      </Anchor>
    )
  },
)

export default TileAdjustButton
